/**
 * The API client, in context.
 *
 * One object for the whole tree, put there by `main.tsx` with the real `fetch` and
 * by `src/test/render.tsx` with a fake one. Nothing imports `createApiClient`
 * directly except those two, and `api/hooks.ts`, which wraps the one it is handed to
 * give a write an abort signal — a component that built its own would be a component
 * a test cannot answer.
 */

import { createContext, use, type ReactNode } from 'react'

import { createApiClient, type ApiClient } from './client.js'

const ApiContext = createContext<ApiClient | null>(null)

export function ApiProvider({
  client,
  children,
}: {
  readonly client: ApiClient
  readonly children: ReactNode
}) {
  return <ApiContext value={client}>{children}</ApiContext>
}

export function useApi(): ApiClient {
  const client = use(ApiContext)
  if (client === null) {
    throw new Error('useApi() outside an <ApiProvider>. main.tsx and test/render.tsx put it there.')
  }
  return client
}

/** The real one. `main.tsx` builds it once; nothing else calls this. */
export function browserApiClient(): ApiClient {
  return createApiClient()
}
